import { CommonModule } from '@angular/common';
import { Component, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleChange, MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AskEvaHistoryListResponse } from 'api-clients/ai-api-client';
import { EvaSuggestedPromptsService } from 'src/app/eva-sidebar/eva-suggested-prompts/eva-suggested-prompts.service';
import { AccountContextService } from 'src/shared/account/account-context.service';
import { EzAlertComponent } from 'src/shared/ez-alert/ez-alert.component';
import { EzEmptyStateComponent } from 'src/shared/ez-empty-state/ez-empty-state.component';
import { ApplicantContext, globalWindow } from 'src/shared/window-globals';
import { EvaSidebarService } from '../eva-sidebar.service';
import { EvaQuestionAnswerService } from '../shared/services/eva-question-answer.service';

@Component({
  selector: 'ez-eva-sidebar-header',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatButtonToggleModule,
    EzAlertComponent,
    EzEmptyStateComponent,
  ],
  templateUrl: './eva-sidebar-header.component.html',
  styleUrls: ['./eva-sidebar-header.component.scss'],
})
export class EvaSidebarHeaderComponent implements OnInit {
  protected readonly selectedMode = signal<'eva' | 'client'>('eva');
  protected readonly hasApplicantContext = signal(false);
  protected readonly applicantContext = signal<ApplicantContext | null>(null);
  protected readonly chatHistory = signal<AskEvaHistoryListResponse | null>(null);

  readonly #accountContextService = inject(AccountContextService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #evaSidebarService = inject(EvaSidebarService);
  readonly #evaQuestionAnswerService = inject(EvaQuestionAnswerService);
  readonly #evaSuggestedPromptsService = inject(EvaSuggestedPromptsService);
  protected readonly isExpanded = this.#evaSidebarService.isExpanded;

  constructor() {
    effect(() => {
      if (this.#evaSidebarService.isOpen()) {
        this.selectedMode.set('eva');
      }
    });
  }

  ngOnInit(): void {
    this.#accountContextService.applicantContext
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((context) => {
        const hasContext = this.#checkApplicantContext(context);
        this.hasApplicantContext.set(hasContext);
        this.applicantContext.set(context || globalWindow.applicantContext || null);
      });

    this.#loadEvaFromHistory();

    this.#evaSidebarService.onChatRenamed$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#loadEvaFromHistory());
  }

  #checkApplicantContext(context?: ApplicantContext | null): boolean {
    return !!context?.ApplicantID || !!globalWindow.applicantContext?.ApplicantID;
  }

  protected onNewChat(): void {
    this.#resetCatchMeUpState();
    this.#evaSidebarService.newChat();
    this.#loadEvaFromHistory();
  }

  protected onExpand(): void {
    this.#evaSidebarService.isExpanded.update(value => !value);
  }

  protected onClose(): void {
    this.#resetQuestionAnswers();
    this.#resetCatchMeUpState();
    this.#evaSuggestedPromptsService.abort();
    this.#evaSidebarService.threadId.set(null);
    this.#evaSidebarService.close();
  }

  protected onModeChange(event: MatButtonToggleChange): void {
    const newMode = event.value as 'eva' | 'client';
    this.selectedMode.set(newMode);
    if (newMode === 'client') {
      this.#evaSidebarService.close();
    }
  }

  #resetQuestionAnswers(): void {
    this.#evaQuestionAnswerService.resetQuestionAnswers('', null);
  }

  #resetCatchMeUpState(): void {
    const index = this.#evaSidebarService.getCatchMeUpIndex();
    if (index != null && index >= 0) {
      this.#evaSidebarService.setCatchMeUpRepressed(true);
    }
  }

  protected loadEva(threadId?: string | null): void {
    this.#evaQuestionAnswerService.abort();
    this.#resetQuestionAnswers();
    const sanitizedThreadId = threadId?.trim() || null;
    this.#evaSidebarService.markOpenedViaHistory();
    this.#evaSidebarService.threadId.set(sanitizedThreadId);
  }

  protected navigateToChatHistory(trigger: MatMenuTrigger): void {
    trigger.closeMenu();
    globalWindow.interop.ezlynx4RouterLink(globalWindow.UrlResolver.MrFive('eva/chat-history'));
  }

  async #loadEvaFromHistory(): Promise<void> {
    this.chatHistory.set(await this.#evaQuestionAnswerService.getHistory());
  }
}
